/* ═══════════════════════════════════════════════════════════════
   Constants — Shared data used across all components
   ═══════════════════════════════════════════════════════════════ */

import type { TokenDef, NumberFormatLevel } from '../shared/types';
import {
  FORMA_TOKEN_DEFAULT_COLOR,
  tokensForScope,
  type FormaTokenDef,
} from '../shared/forma-placeholders';

export const FONTS: string[] = [
  'Arial',
  'Arial Narrow',
  'Calibri',
  'Cambria',
  'Century Gothic',
  'Courier New',
  'Garamond',
  'Georgia',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
];

/* Derived from the ONE shared vocabulary (src/shared/forma-placeholders.ts
   FORMA_TOKENS) — add a token there, never here. */
const toTokenDef = (t: FormaTokenDef): TokenDef => ({
  id: t.id,
  label: t.label,
  value: t.value,
  color: t.color || FORMA_TOKEN_DEFAULT_COLOR,
});

export const TOKENS: TokenDef[] = tokensForScope(['spec']).map(toTokenDef);

export const POST_TOKENS: TokenDef[] = [];

/* Project-linked tokens — fill in from the actual project at export time.
   The word engine resolves them from the projectMeta map provided by the
   project-spec export pipeline (same data the smart-block engine uses).
   In the designer they preview with sample values. */
export const PROJECT_TOKENS: TokenDef[] = tokensForScope([
  'firm',
  'client',
  'project',
  'issuance',
]).map(toTokenDef);

export const ALL_TOKENS: TokenDef[] = [...TOKENS, ...PROJECT_TOKENS, ...POST_TOKENS];

export const CSI_NUMBER_FORMATS: NumberFormatLevel[] = [
  {
    level: 1,
    options: [
      {
        id: 'part_dash',
        label: 'PART 1 -',
        lvlText: 'PART %1 -',
        numFmt: 'decimal',
        suff: 'space',
      },
      { id: 'part_space', label: 'PART 1', lvlText: 'PART %1', numFmt: 'decimal', suff: 'space' },
    ],
  },
  {
    level: 2,
    options: [
      { id: '1.01', label: '1.01', lvlText: '%1.%2', numFmt: 'decimalZero', suff: 'tab' },
      { id: '1.1', label: '1.1', lvlText: '%1.%2', numFmt: 'decimal', suff: 'tab' },
    ],
  },
  {
    level: 3,
    options: [{ id: 'A.', label: 'A.', lvlText: '%3.', numFmt: 'upperLetter', suff: 'tab' }],
  },
  {
    level: 4,
    options: [{ id: '1.', label: '1.', lvlText: '%4.', numFmt: 'decimal', suff: 'tab' }],
  },
  {
    level: 5,
    options: [{ id: 'a.', label: 'a.', lvlText: '%5.', numFmt: 'lowerLetter', suff: 'tab' }],
  },
  {
    level: 6,
    options: [
      { id: '1)', label: '1)', lvlText: '%6)', numFmt: 'decimal', suff: 'tab' },
      { id: '(1)', label: '(1)', lvlText: '(%6)', numFmt: 'decimal', suff: 'tab' },
    ],
  },
  {
    level: 7,
    options: [
      { id: 'a)', label: 'a)', lvlText: '%7)', numFmt: 'lowerLetter', suff: 'tab' },
      { id: '(a)', label: '(a)', lvlText: '(%7)', numFmt: 'lowerLetter', suff: 'tab' },
    ],
  },
  {
    level: 8,
    options: [
      { id: '(1)', label: '(1)', lvlText: '(%8)', numFmt: 'decimal', suff: 'tab' },
      { id: '1)', label: '1)', lvlText: '%8)', numFmt: 'decimal', suff: 'tab' },
    ],
  },
  {
    level: 9,
    options: [
      { id: '(a)', label: '(a)', lvlText: '(%9)', numFmt: 'lowerLetter', suff: 'tab' },
      { id: 'a)', label: 'a)', lvlText: '%9)', numFmt: 'lowerLetter', suff: 'tab' },
    ],
  },
];

export const SC = 0.82;
export const PPI = 96;

// ── Expose as globals for legacy JSX files ──
(window as any).FONTS = FONTS;
(window as any).TOKENS = TOKENS;
(window as any).POST_TOKENS = POST_TOKENS;
(window as any).ALL_TOKENS = ALL_TOKENS;
(window as any).CSI_NUMBER_FORMATS = CSI_NUMBER_FORMATS;
(window as any).SC = SC;
(window as any).PPI = PPI;
